import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CommonDropdownComponent } from '@lib-ui/common-dropdawn/common-dropdown.component';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormArray, FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from '@lib-services/message.service';
import { PersonService } from '@lib-services/person.service';
import { IPerson, IPersonFormData, IPersonUpdateResponse } from '@lib-typing/person.interface';
import { Person } from '@libs-classes/person.class';
import { Role } from '@libs-enums/role.enum';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { EnumService } from '@lib-services/enum.service';
import { GroupsSelectorService } from '@lib-services/groups-selector.service';
import { LoaderComponent } from '@lib-ui/loader/loader.component';
import { AuthService } from '@lib-ui/auth/auth.service';
import { roleValidator } from '../../../../../../libs/validators/role-validator';
import { CompanySelectorService } from '@lib-services/company-selector.service';
import { ScheduleParamsComponent } from '@lib-ui/schedule-params/schedule-params.component';
import { filter, Observable, switchMap } from 'rxjs';
import { CloseButtonComponent } from '@lib-ui/close-button/close-button.component';
import { GroupSelectorComponent } from '@lib-ui/group-selector/group-selector.component';

@Component({
    selector: 'app-person-editor',
    standalone: true,
    imports: [
        CommonModule,
        CommonDropdownComponent,
        MatButton,
        MatDialogContent,
        MatFormField,
        MatInput,
        MatLabel,
        ReactiveFormsModule,
        MatOption,
        MatSelect,
        LoaderComponent,
        MatError,
        CloseButtonComponent,
        MatDialogTitle,
        GroupSelectorComponent,
    ],
    templateUrl: './person-editor.component.html',
    styleUrl: './person-editor.component.scss'
})
export class PersonEditorComponent implements OnInit {
    person = new Person({} as IPerson);
    loading = signal(false);

    readonly dialogRef = inject(MatDialogRef<PersonEditorComponent>);
    readonly dialog = inject(MatDialog);
    readonly fb = inject(FormBuilder);
    readonly personService = inject(PersonService);
    readonly authService = inject(AuthService);
    protected groupsSelectorService = inject(GroupsSelectorService);
    protected companySelectorService = inject(CompanySelectorService);
    protected messagesService = inject(MessageService);
    protected enumService = inject(EnumService);
    public data: {
        personId?: number,
    } = inject(MAT_DIALOG_DATA);
    protected readonly Role = Role;
    private updatingForm = false;
    currentUser: IPerson | null = null;

    personForm = this.fb.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        login: ['', Validators.required],
        password: [''],
        groupId: [0, Validators.required],
        role: [Role.executor, Validators.required],
        phone: [''],
        managesGroupsIds: [[0]]
    });

    ngOnInit(): void {
        if (this.data.personId) {
            this.loading.set(true);
            this.personService.getPerson(this.data.personId).subscribe( {
                next: data => {
                    this.person.data = data;
                    const formData = this.person.formData();
                    this.personForm.setValue(formData);
                },
                error: error => {this.messagesService.errorMessage(error.message);}
            }).add(() => this.loading.set(false));
        }

        this.currentUser = this.authService.user().data;

        this.personForm.valueChanges.subscribe(() =>  {
            if (this.updatingForm) {
                return;
            }

            this.updatingForm = true;
            const role = this.personForm.controls.role.value;

            // Добавление валидатора для поля role
            this.personForm.controls.role.setValidators([
                Validators.required,
                roleValidator(this.currentUser?.role || '')
            ]);

            // Сброс значений всех полей формы, кроме роли
            if (!this.person.data.id && this.personForm.controls.role.dirty) {
                // Сохраняем только значение поля 'role', все остальные сбрасываем
                const currentRoleValue = this.personForm.controls.role.value;
                this.personForm.reset({
                    role: currentRoleValue,
                });
            }

            switch (role) {
            case Role.executor:
                this.personForm.controls.login.setValidators([Validators.required]);
                this.personForm.controls.groupId.setValidators([Validators.required]);
                this.personForm.controls.role.clearValidators();
                break;
            case Role.customer:
                this.personForm.controls.login.clearValidators();
                this.personForm.controls.groupId.clearValidators();
                this.personForm.controls.role.clearValidators();
                break;
            case Role.manager:
                this.personForm.controls.login.setValidators([Validators.required]);
                this.personForm.controls.groupId.clearValidators();

                break;
            case Role.admin:
                this.personForm.controls.login.setValidators([Validators.required]);
                this.personForm.controls.groupId.clearValidators();
                break;
            }

            // Обновление валидности для каждого контрола
            Object.values(this.personForm.controls).forEach((control: FormControl | FormArray) => {
                control.updateValueAndValidity();
            });

            this.updatingForm = false;
        });

    }

    onSubmit(): void {
        if (!this.personForm.valid) {
            return;
        }

        if(!this.person.data.scheduleParams && this.personForm.controls.role.value === Role.executor) {
            this.messagesService.confirmMessage({
                text: 'Вы хотите продолжить сохранение без расписания',
                primaryText: 'Продолжить',
                secondaryText: 'Задать расписание',
                showSecondaryButton: true,
            }).pipe(
                filter(data => data)
            ).subscribe({
                next: () => this.editPerson(),
            });
        } else {
            this.editPerson();
        }
    }

    editPerson(): void {
        this.loading.set(true);
        const personData = this.personForm.value;

        const request: Observable<IPerson | IPersonUpdateResponse> = this.person.data.id
            ? this.personService.update(this.person.dataForUpdate(personData as IPersonFormData))
            :  this.personService.create(this.person.dataForCreate(personData as IPersonFormData));

        request.subscribe({
            next: result => {
                if ('tokens' in result && result.tokens) {
                    this.authService.saveToLocalStorage(result.tokens);
                }

                this.messagesService.successMessage('Контакт успешно сохранен');
                this.dialogRef.close(result);},
            error: error => {this.messagesService.errorMessage(error.message);}
        }).add(() => this.loading.set(false));
    }

    openSchedule(): void {
        this.dialog.open(ScheduleParamsComponent, {
            maxWidth: '550px',
            width: '95%',
            maxHeight: '440px',
            height: '95%',
            data: this.person,
        });
    }

    deletePerson(id: number): void {
        if(!this.person.data) {return;}

        this.messagesService.confirmMessage({
            title: 'Удаление контакта',
            text: 'Вы действительно хотите удалить контакт?',
            primaryText: 'Удалить',
            secondaryText: 'Отмена',
            showSecondaryButton: true,
        }).pipe(
            filter(result => result),
            switchMap(() => {
                return this.personService.deletePersons([id]);
            }),
        ).subscribe({
            next: () => {
                this.messagesService.successMessage('Контакт успешно удален');
                this.dialogRef.close(true);
            },
            error: () => this.messagesService.errorMessage('При удалении произошла ошибка')
        });
    }
}
